import Highcharts from "highcharts";
import { toSafeNumber } from "../data/transformers";

type ForceBarBreakdownInput = {
  task_label?: string;
  avg_duration_human?: Array<string | number | null | undefined>;
  order?: number;
};

type ForceBarSeriesInput = {
  name?: string;
  data?: Array<number | string | null | undefined>;
  order?: number;
  cases_participated?: number;
  breakdown?: ForceBarBreakdownInput[];
};

type ForceBarSummary = {
  total_cases_analyzed?: number;
  complete_cycle_cases?: number;
  incomplete_cycle_cases?: number;
  calculation_method?: string;
};

type ForceBarInput = {
  avg_total_lifecycle_human?: string;
  series?: ForceBarSeriesInput[];
  summary?: ForceBarSummary;
};

type ChartRow = {
  key: string;
  label: string;
  kind: "main" | "breakdown";
  stageName?: string;
};

type SeriesDescriptor = {
  name: string;
  color: string;
  values: Array<number | null>;
  isBreakdown: boolean;
  legendIndex?: number;
  parentStage?: string;
  labelText?: string;
  casesParticipated?: number;
};

const FORCE_BAR_PLUGIN_FLAG = "__forceBarArrowPluginApplied";
const highchartsRuntime = Highcharts as unknown as Record<string, any>;

const STAGE_COLORS = [
  "#E11D48",
  "#F97316",
  "#EAB308",
  "#22C55E",
  "#06B6D4",
  "#3B82F6",
  "#8B5CF6",
  "#14B8A6",
];

const getStageColor = (index: number): string =>
  STAGE_COLORS[index % STAGE_COLORS.length] ?? "#3B82F6";

if (!(Highcharts as Record<string, unknown>)[FORCE_BAR_PLUGIN_FLAG]) {
  Highcharts.addEvent(
    highchartsRuntime.seriesTypes.column,
    "afterColumnTranslate",
    function afterColumnTranslate() {
      const series = this as any;

      if (series.type !== "bar") {
        return;
      }

      const options = series.options ?? {};
      const headSize = Number(options.headSize ?? 0);

      if (!headSize) {
        return;
      }

      (series.points ?? []).forEach((point: any) => {
        const shapeArgs = point?.shapeArgs;

        if (!shapeArgs) {
          return;
        }

        const w = Number(shapeArgs.width ?? 0);
        const h = Number(shapeArgs.height ?? 0);
        const x = Number(shapeArgs.x ?? 0);
        const y = Number(shapeArgs.y ?? 0);

        const cutLeft = false;
        const cutRight = false;
        const len = Math.max(0, Math.min(Math.abs(headSize), w / 2));

        if (!cutLeft && !cutRight) {
          return;
        }

        point.dlBox = point.shapeArgs;
        point.shapeType = "path";

        const leftInset = cutLeft ? len : 0;
        const rightInset = cutRight ? len : 0;
        point.shapeArgs = {
          d: [
            ["M", x + leftInset, y],
            ["L", x + w - rightInset, y],
            ...(cutRight ? [["L", x + w, y + h / 2]] : []),
            ["L", x + w - rightInset, y + h],
            ["L", x + leftInset, y + h],
            ...(cutLeft ? [["L", x, y + h / 2]] : []),
            ["Z"],
          ],
        };
      });
    },
  );

  (Highcharts as Record<string, unknown>)[FORCE_BAR_PLUGIN_FLAG] = true;
}

const sortByOrder = <T extends { order?: number }>(items: T[]): T[] =>
  [...items].sort((a, b) => {
    const left = Number.isFinite(Number(a.order))
      ? Number(a.order)
      : Number.MAX_SAFE_INTEGER;
    const right = Number.isFinite(Number(b.order))
      ? Number(b.order)
      : Number.MAX_SAFE_INTEGER;

    return left - right;
  });

const toRgba = (hexColor: string, alpha: number): string => {
  const normalized = hexColor.replace("#", "");
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => `${char}${char}`)
          .join("")
      : normalized;

  if (expanded.length !== 6) {
    return hexColor;
  }

  const r = parseInt(expanded.slice(0, 2), 16);
  const g = parseInt(expanded.slice(2, 4), 16);
  const b = parseInt(expanded.slice(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${Math.min(Math.max(alpha, 0), 1)})`;
};

const parseDurationToHours = (value: unknown): number => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? Math.max(value, 0) : 0;
  }

  if (typeof value !== "string") {
    return 0;
  }

  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return 0;
  }

  const dayMatch = normalized.match(/(-?\d+(?:\.\d+)?)\s*d/);
  const hourMatch = normalized.match(/(-?\d+(?:\.\d+)?)\s*h/);
  const minuteMatch = normalized.match(/(-?\d+(?:\.\d+)?)\s*m/);

  if (!dayMatch && !hourMatch && !minuteMatch) {
    const plainNumber = Number(normalized);
    return Number.isFinite(plainNumber) ? Math.max(plainNumber, 0) : 0;
  }

  const days = dayMatch ? toSafeNumber(dayMatch[1]) : 0;
  const hours = hourMatch ? toSafeNumber(hourMatch[1]) : 0;
  const minutes = minuteMatch ? toSafeNumber(minuteMatch[1]) : 0;

  return Math.max(days * 24 + hours + minutes / 60, 0);
};

const formatForceBarValue = (value: number): string => {
  if (!Number.isFinite(value)) {
    return "0";
  }

  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded)
    ? `${rounded}`
    : rounded
        .toFixed(2)
        .replace(/\.0+$/, "")
        .replace(/(\.\d*?)0+$/, "$1");
};

const formatDurationHuman = (hoursValue: number): string => {
  const totalMinutes = Math.max(0, Math.round(hoursValue * 60));
  const days = Math.floor(totalMinutes / (24 * 60));
  const remainingMinutes = totalMinutes - days * 24 * 60;
  const hours = Math.floor(remainingMinutes / 60);
  const minutes = remainingMinutes - hours * 60;

  return `${days}d ${hours}h ${minutes}m`;
};

const formatDurationCompact = (hoursValue: number): string => {
  const totalMinutes = Math.max(0, Math.round(hoursValue * 60));
  const days = Math.floor(totalMinutes / (24 * 60));
  const remainingMinutes = totalMinutes - days * 24 * 60;
  const hours = Math.floor(remainingMinutes / 60);

  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  return `${formatForceBarValue(hoursValue)}h`;
};

const buildSummarySubtitle = (summary: ForceBarSummary | undefined): string => {
  if (!summary) return "";

  const stats: Array<{ label: string; value: string; color: string }> = [];

  if (summary.total_cases_analyzed !== undefined)
    stats.push({
      label: "Total Cases",
      value: summary.total_cases_analyzed.toLocaleString(),
      color: "#3B82F6",
    });
  if (summary.complete_cycle_cases !== undefined)
    stats.push({
      label: "Complete Cycle",
      value: summary.complete_cycle_cases.toLocaleString(),
      color: "#22C55E",
    });
  if (summary.incomplete_cycle_cases !== undefined)
    stats.push({
      label: "Incomplete Cycle",
      value: summary.incomplete_cycle_cases.toLocaleString(),
      color: "#F97316",
    });

  const chips = stats
    .map(
      (s) =>
        `<span style="display:inline-flex;align-items:center;gap:4px;background:#F1F5F9;border-radius:6px;padding:2px 8px;margin-right:6px;">` +
        `<span style="font-weight:700;color:${s.color};font-size:12px;">${s.value}</span>` +
        `<span style="color:#64748B;font-size:11px;">${s.label}</span>` +
        `</span>`,
    )
    .join("");

  const methodNote = summary.calculation_method
    ? `<span style="color:#94A3B8;font-size:10px;font-style:italic;margin-left:4px;">${summary.calculation_method}</span>`
    : "";

  return `<div style="display:flex;align-items:center;flex-wrap:wrap;gap:2px;margin-top:2px;">${chips}${methodNote}</div>`;
};

const buildForceBarOptions = (data: unknown): Highcharts.Options => {
  const forceBarData = (data ?? {}) as ForceBarInput;
  const sourceSeries = Array.isArray(forceBarData.series)
    ? sortByOrder(forceBarData.series)
    : [];
  const summarySubtitleHtml = buildSummarySubtitle(forceBarData.summary);
  const totalLifecycleHuman = String(
    forceBarData.avg_total_lifecycle_human ?? "1d 24h 0m",
  ).trim();
  const computedTotalHours = sourceSeries.reduce((total, stage) => {
    const firstValue = Array.isArray(stage.data) ? stage.data[0] : 0;
    return total + parseDurationToHours(firstValue);
  }, 0);
  const totalLifecycleDisplay =
    totalLifecycleHuman.length > 0
      ? totalLifecycleHuman
      : formatDurationHuman(computedTotalHours);

  const rows: ChartRow[] = [
    {
      key: "timeline",
      label: "Timeline",
      kind: "main",
    },
  ];

  sourceSeries.forEach((stage, stageIndex) => {
    const breakdownItems = Array.isArray(stage.breakdown)
      ? sortByOrder(stage.breakdown)
      : [];

    if (breakdownItems.length > 0) {
      rows.push({
        key: `breakdown-${stageIndex}`,
        label: `${String(stage.name ?? `Stage ${stageIndex + 1}`)} breakdown`,
        kind: "breakdown",
        stageName: String(stage.name ?? ""),
      });
    }
  });

  const categories = rows.map((row) => row.label);
  const rowIndexByKey = new Map(rows.map((row, index) => [row.key, index]));

  const mainSeriesDescriptors: SeriesDescriptor[] = sourceSeries.map(
    (stage, index) => {
      const values = rows.map((_row, rowIndex) => {
        if (rowIndex !== 0) {
          return null;
        }

        const firstValue = Array.isArray(stage.data) ? stage.data[0] : 0;
        return parseDurationToHours(firstValue);
      });

      return {
        name: String(stage.name ?? `Stage ${index + 1}`),
        color: getStageColor(index),
        values,
        isBreakdown: false,
        legendIndex: index,
        casesParticipated:
          typeof stage.cases_participated === "number"
            ? stage.cases_participated
            : undefined,
      };
    },
  );

  const breakdownDescriptors: SeriesDescriptor[] = sourceSeries.flatMap(
    (stage, stageIndex) => {
      const breakdownItems = Array.isArray(stage.breakdown)
        ? sortByOrder(stage.breakdown)
        : [];
      if (breakdownItems.length === 0) {
        return [];
      }

      const rowIndex = rowIndexByKey.get(`breakdown-${stageIndex}`);
      if (rowIndex === undefined) {
        return [];
      }

      const stageName = String(stage.name ?? `Stage ${stageIndex + 1}`);
      const baseColor = getStageColor(stageIndex);

      return breakdownItems.map((task, taskIndex) => {
        const values = rows.map((_row, idx) => {
          if (idx !== rowIndex) {
            return null;
          }

          const firstValue = Array.isArray(task.avg_duration_human)
            ? task.avg_duration_human[0]
            : 0;
          return parseDurationToHours(firstValue);
        });

        const taskLabel = String(task.task_label ?? `Task ${taskIndex + 1}`);

        return {
          name: `${stageName} - ${taskLabel}`,
          color: toRgba(baseColor, Math.max(0.88 - taskIndex * 0.12, 0.42)),
          values,
          isBreakdown: true,
          parentStage: stageName,
          labelText: taskLabel,
        };
      });
    },
  );

  const seriesDescriptors = [...mainSeriesDescriptors, ...breakdownDescriptors];
  const series: Highcharts.SeriesOptionsType[] = seriesDescriptors.map(
    (descriptor) => ({
      type: "bar",
      name: descriptor.name,
      showInLegend: !descriptor.isBreakdown,
      legendIndex: descriptor.legendIndex,
      color: descriptor.color,
      data: descriptor.values.map((value) =>
        value === null
          ? null
          : {
              y: value,
              custom: {
                isBreakdown: descriptor.isBreakdown,
                isTimelineStart:
                  !descriptor.isBreakdown && descriptor.legendIndex === 0,
                totalLifecycleDisplay,
                parentStage: descriptor.parentStage,
                labelText: descriptor.labelText,
                casesParticipated: descriptor.casesParticipated,
              },
            },
      ),
      ...({
        headSize: 7,
        topMargin: 0,
        bottomMargin: 0,
      } as Record<string, number>),
    }),
  );

  return {
    chart: {
      type: "bar",
      animation: false,
      events: {
        render: function render() {
          const chart = this as Highcharts.Chart & {
            forceBarLifecycleLabel?: Highcharts.SVGElement;
          };

          const timelinePoints = (chart.series ?? []).flatMap((entry) =>
            (entry.points ?? []).filter((point: any) => {
              const pointOptions = point?.options?.custom ?? {};
              return !pointOptions.isBreakdown && point.index === 0;
            }),
          ) as Highcharts.Point[];

          const pointBoxes = timelinePoints
            .map((point) => {
              const runtimePoint = point as any;
              const box = runtimePoint?.dlBox ?? runtimePoint?.shapeArgs;
              if (!box) {
                return null;
              }

              const x = chart.plotLeft + Number(box.x ?? 0);
              const y = chart.plotTop + Number(box.y ?? 0);
              const width = Number(box.width ?? 0);
              const height = Number(box.height ?? 0);

              if (width <= 0 || height <= 0) {
                return null;
              }

              return { x, y, width, height };
            })
            .filter(Boolean) as Array<{
            x: number;
            y: number;
            width: number;
            height: number;
          }>;

          if (pointBoxes.length === 0) {
            chart.forceBarLifecycleLabel?.destroy();
            chart.forceBarLifecycleLabel = undefined;
            return;
          }

          const text = `Total lifecycle: ${totalLifecycleDisplay}`;

          if (!chart.forceBarLifecycleLabel) {
            chart.forceBarLifecycleLabel = chart.renderer
              .label(text, 0, 0)
              .attr({
                padding: 4,
                r: 10,
                fill: "#E2E8F0",
                zIndex: 7,
              })
              .css({
                color: "#0F172A",
                fontSize: "11px",
                fontWeight: "700",
              })
              .add();
          }

          chart.forceBarLifecycleLabel.attr({ text });
          const labelBox = chart.forceBarLifecycleLabel.getBBox();
          const labelWidth = Number(labelBox.width ?? 0);
          const labelHeight = Number(labelBox.height ?? 0);
          const horizontalPadding = 8;
          const preferredRightX =
            chart.plotLeft + chart.plotWidth - labelWidth - horizontalPadding;
          const lcX = Math.max(
            chart.plotLeft + horizontalPadding,
            preferredRightX - 30,
          );
          const lcY = Math.max(2, chart.plotTop - labelHeight - 8);
          chart.forceBarLifecycleLabel.attr({ x: lcX, y: lcY });
        },
      },
    },
    // title: {
    //   text: "",
    //   align: "left",
    // },
    // subtitle: {
    //   text: summarySubtitleHtml,
    //   useHTML: true,
    //   align: "left",
    // },
    xAxis: {
      categories,
      title: {
        text: null,
      },
      lineWidth: 0,
      tickWidth: 0,
      labels: {
        enabled: true,
        style: {
          color: "#334155",
          fontWeight: "600",
          fontSize: "11px",
        },
      },
    },
    yAxis: {
      min: 0,
      reversedStacks: false,
      title: {
        text: "Hours",
        style: {
          color: "#64748B",
          fontSize: "11px",
          fontWeight: "600",
        },
      },
      labels: {
        overflow: "justify",
        style: {
          color: "#64748B",
          fontSize: "11px",
        },
      },
      gridLineDashStyle: "ShortDot",
      startOnTick: false,
      endOnTick: false,
    },
    tooltip: {
      shared: false,
      split: false,
      useHTML: true,
      borderWidth: 0,
      borderRadius: 10,
      backgroundColor: "rgba(15, 23, 42, 0.92)",
      shadow: {
        color: "rgba(2, 6, 23, 0.35)",
        offsetX: 0,
        offsetY: 8,
        opacity: 0.3,
        width: 16,
      },
      padding: 0,
      formatter: function formatter() {
        const ctx = this as any;
        const pointOptions = ctx?.point?.options?.custom ?? {};
        const isBreakdown = Boolean(pointOptions.isBreakdown);
        const rawSeriesName = String(this.series?.name ?? "");
        const seriesName = isBreakdown
          ? String(pointOptions.labelText ?? rawSeriesName)
          : rawSeriesName;
        const stageName = String(pointOptions.parentStage ?? "");
        // const category = String(this.x ?? "") || "Timeline";
        const numericValue = toSafeNumber(this.y);
        if (numericValue <= 0) {
          return false;
        }
        const value = formatForceBarValue(numericValue);
        const unit = numericValue === 1 ? "hour" : "hours";
        const casesParticipated = pointOptions.casesParticipated as
          | number
          | undefined;

        return `
          <div style="padding:10px 12px; min-width: 220px; color:#f8fafc; font-size:12px; line-height:1.35; z-index:99;">
            <div style="font-weight:700; font-size:12px; margin-bottom:4px; color:#ffffeff;">${seriesName}</div>
            ${
              isBreakdown && stageName
                ? `<div style="font-size:11px; opacity:0.78; margin-bottom:6px;">${stageName}</div>`
                : ""
            }
            <div style="display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:3px;">
              <span style="font-weight:700; font-size:14px;">${value} ${unit}</span>
            </div>
            <div style="opacity:0.7; font-size:11px;">${formatDurationHuman(numericValue)}</div>
            ${
              casesParticipated !== undefined
                ? `<div style="margin-top:6px; padding-top:6px; border-top:1px solid rgba(255,255,255,0.15); display:flex; align-items:center; gap:6px;">
                    <span style="opacity:0.7; font-size:11px;">Cases analyzed:</span>
                    <span style="font-weight:700; font-size:12px; color:#7DD3FC;">${casesParticipated.toLocaleString()}</span>
                  </div>`
                : ""
            }
          </div>
        `;
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 0,
        pointWidth: 35,
        pointPadding: 0.1,
        groupPadding: 0.12,
      },
      series: {
        stacking: "normal",
        borderWidth: 0,
        states: {
          inactive: {
            opacity: 0.38,
          },
          hover: {
            enabled: true,
            brightness: 0.05,
            halo: {
              size: 0,
            },
          },
        },
        dataLabels: [
          {
            enabled: true,
            inside: true,
            useHTML: true,
            style: {
              color: "#0B1220",
              fontWeight: "700",
              textOutline: "none",
              fontSize: "10px",
            },
            formatter: function formatter() {
              const ctx = this as any;
              const value = toSafeNumber(ctx.y);
              if (value <= 0) {
                return "";
              }

              return formatDurationCompact(value);
            },
          },
          {
            enabled: true,
            inside: false,
            align: "left" as const,
            x: 6,
            useHTML: true,
            style: {
              textOutline: "none",
            },
            formatter: function formatter() {
              const ctx = this as any;
              const cases = ctx?.point?.options?.custom?.casesParticipated as
                | number
                | undefined;
              if (cases === undefined) {
                return "";
              }
              return "";
            },
          },
        ],
      },
    },
    legend: {
      enabled: true,
      reversed: false,
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemDistance: 12,
      symbolWidth: 12,
      symbolHeight: 12,
      symbolRadius: 2,
      itemStyle: {
        fontSize: "11px",
        fontWeight: "600",
        color: "#334155",
      },
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: true,
      fallbackToExportServer: true,
      buttons: {
        contextButton: {
          align: "right",
          verticalAlign: "top",
          y: -43,
          x: -20,
        },
      },
    },
    accessibility: {
      enabled: false,
    },
    series,
  };
};

export function renderForceBar(
  data: unknown,
  spec: unknown,
): Highcharts.Options {
  const options = buildForceBarOptions(data);

  return {
    ...options,
    title: { text: "" },
  };
}
